/**
 * The signed-in menu in the masthead, ported from the Nuxt Dropdown component.
 *
 * Only console pages render the mount point, so the docs pages never call the accounts
 * API or show a half-populated menu.
 */

import { getAccount, type Account } from './api.ts';
import { signOut } from './auth.ts';
import { consoleConfig } from './config.ts';
import { clear, el } from './ui.ts';

function displayName(account: Account): string {
  const full = [account.firstName, account.lastName].filter(Boolean).join(' ').trim();
  return full || account.name || account.email || 'Account';
}

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '?';
  const second = parts.length > 1 ? (parts[parts.length - 1] as string).charAt(0) : '';
  return (first + second).toUpperCase();
}

export async function initAccountMenu(): Promise<void> {
  const host = document.querySelector<HTMLElement>('[data-console-account]');
  if (!host) return;

  let account: Account;
  try {
    account = await getAccount();
  } catch {
    // A failed lookup should not block the page — the views handle their own auth errors.
    return;
  }

  const { accountsAppBase, streamsAppBase } = consoleConfig();
  const name = displayName(account);

  const avatar = account.avatar
    ? el('img', { class: 'account-avatar', src: account.avatar, alt: '', decoding: 'async' })
    : el('span', { class: 'account-avatar is-placeholder', text: initials(name) });

  const trigger = el(
    'button',
    {
      class: 'account-trigger',
      type: 'button',
      'aria-expanded': 'false',
      'aria-haspopup': 'menu',
      'aria-controls': 'account-menu',
    },
    [
      avatar,
      el('span', { class: 'account-name', text: name }),
      el('span', { class: 'account-caret', 'aria-hidden': 'true' }),
    ],
  );

  const signout = el('button', { class: 'account-item is-signout', type: 'button', role: 'menuitem', text: 'Sign out' });
  signout.addEventListener('click', signOut);

  const menu = el('div', { class: 'account-menu', id: 'account-menu', role: 'menu', hidden: true }, [
    el('div', { class: 'account-menu-head' }, [
      el('p', { class: 'account-menu-name', text: name }),
      account.email ? el('p', { class: 'account-menu-email', text: account.email }) : null,
    ]),
    el('a', { class: 'account-item', role: 'menuitem', href: `${streamsAppBase}/`, text: 'Dashboard' }),
    el('a', { class: 'account-item', role: 'menuitem', href: `${accountsAppBase}/`, text: 'Account settings' }),
    signout,
  ]);

  const setOpen = (open: boolean) => {
    menu.hidden = !open;
    trigger.setAttribute('aria-expanded', String(open));
  };

  trigger.addEventListener('click', () => setOpen(menu.hidden));

  document.addEventListener('click', (event) => {
    if (!host.contains(event.target as Node)) setOpen(false);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false);
  });

  clear(host);
  host.append(trigger, menu);
  host.hidden = false;
}
